/**
 * Enhanced error types for Player API (BEWOTEC SOAP)
 *
 * Provides structured error handling with specific error classes for different failure scenarios.
 */

/**
 * Base custom error class for Player API errors
 *
 * @example
 * throw new PlayerApiError('Failed to fetch data', 'API_ERROR', 'GetProductList');
 */
export class PlayerApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public endpoint?: string,
    public response?: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'PlayerApiError';

    // Maintains proper stack trace for where error was thrown (only available in V8 engines)
    const ErrorConstructor = Error as unknown as {
      captureStackTrace?: (
        target: object,
        constructor: new (...args: unknown[]) => unknown
      ) => void;
    };
    if (typeof ErrorConstructor.captureStackTrace === 'function') {
      ErrorConstructor.captureStackTrace(
        this,
        this.constructor as new (...args: unknown[]) => unknown
      );
    }
  }
}

/**
 * SOAP fault error
 *
 * Thrown when the SOAP API returns a fault response.
 *
 * @example
 * throw new SoapFaultError(
 *   'Authentication failed',
 *   'Client.Authentication',
 *   'Invalid credentials',
 *   { attemptedUser: 'user@example.com' }
 * );
 */
export class SoapFaultError extends PlayerApiError {
  constructor(
    message: string,
    public faultCode: string,
    public faultString: string,
    public detail?: unknown
  ) {
    super(message, 'SOAP_FAULT');
    this.name = 'SoapFaultError';
  }
}

/**
 * Network error (timeout, connection refused, etc.)
 *
 * Thrown when network-level errors occur during API calls.
 *
 * @example
 * // Timeout error
 * throw new NetworkError('Request timeout after 30000ms', undefined, true);
 *
 * // Connection error
 * throw new NetworkError('Connection refused', originalError, false);
 */
export class NetworkError extends PlayerApiError {
  constructor(
    message: string,
    public originalError?: Error,
    public isTimeout = false
  ) {
    super(message, isTimeout ? 'NETWORK_TIMEOUT' : 'NETWORK_ERROR');
    this.name = 'NetworkError';
  }
}

/**
 * Validation error from API or client-side validation
 *
 * Thrown when input validation fails, either client-side or from API response.
 *
 * @example
 * throw new ValidationError('Invalid passenger configuration', [
 *   { field: 'adults', message: 'At least 1 adult is required' },
 *   { field: 'children[0].age', message: 'Age must be between 0 and 17' }
 * ]);
 */
export class ValidationError extends PlayerApiError {
  constructor(
    message: string,
    public validationErrors: Array<{
      field?: string;
      message: string;
    }>
  ) {
    super(message, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

/**
 * Configuration error (missing auth key, invalid config, etc.)
 *
 * Thrown when required configuration is missing or invalid.
 *
 * @example
 * throw new ConfigurationError(
 *   'Required environment variables are missing',
 *   ['VITE_BEWOTEC_AUTH_KEY']
 * );
 */
export class ConfigurationError extends PlayerApiError {
  constructor(
    message: string,
    public missingConfig?: string[]
  ) {
    super(message, 'CONFIGURATION_ERROR');
    this.name = 'ConfigurationError';
  }
}

/**
 * Base error for DaVinci API
 *
 * Thrown for general DaVinci API errors.
 *
 * @example
 * throw new DaVinciApiError('API request failed', '500', '/PerformAction');
 */
export class DaVinciApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public endpoint?: string,
    public response?: string
  ) {
    super(message);
    this.name = 'DaVinciApiError';

    const ErrorConstructor = Error as unknown as {
      captureStackTrace?: (
        target: object,
        constructor: new (...args: unknown[]) => unknown
      ) => void;
    };
    if (typeof ErrorConstructor.captureStackTrace === 'function') {
      ErrorConstructor.captureStackTrace(
        this,
        this.constructor as new (...args: unknown[]) => unknown
      );
    }
  }
}

/**
 * DaVinci network error (timeout, connection refused, etc.)
 *
 * Thrown when network-level errors occur during DaVinci API calls.
 *
 * @example
 * // Timeout error
 * throw new DaVinciNetworkError('Request timeout after 30000ms', true);
 *
 * // Connection error
 * throw new DaVinciNetworkError('Connection refused', false);
 */
export class DaVinciNetworkError extends DaVinciApiError {
  constructor(
    message: string,
    public isTimeout = false
  ) {
    super(message, isTimeout ? 'NETWORK_TIMEOUT' : 'NETWORK_ERROR');
    this.name = 'DaVinciNetworkError';
  }
}

/**
 * DaVinci return code error
 *
 * Thrown when DaVinci API returns an error return code.
 *
 * @example
 * throw new DaVinciReturnCodeError(999, 'Room unavailable');
 */
export class DaVinciReturnCodeError extends DaVinciApiError {
  constructor(
    public returnCode: number,
    public returnMessage?: string
  ) {
    super(
      `DaVinci returned error code ${returnCode}: ${returnMessage || 'Unknown error'}`,
      returnCode.toString()
    );
    this.name = 'DaVinciReturnCodeError';
  }
}
